import { Link, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import api from '@/lib/api'
import { devicesQueryOptions } from '@/lib/queries'
import { channelStripeClass, getDeviceLabel, getDeviceState, stateDotClass } from '@/lib/devices'
import {
  Home,
  MessageSquare,
  Smartphone,
  Key,
  Settings,
  User,
  LogOut,
  ChevronDown,
  Plus,
  Webhook,
  X,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState } from 'react'

interface NavigationItem {
  name: string
  href: string
  icon: any
  badge?: string | number
}

interface SidebarProps {
  onClose?: () => void
}

export default function Sidebar({ onClose }: SidebarProps) {
  const location = useLocation()
  const { user, logout } = useAuth()
  const [showRecentDevices, setShowRecentDevices] = useState(true)

  const { data: deviceData } = useQuery({
    ...devicesQueryOptions(),
    select: (data) => (Array.isArray(data) ? data : []),
  })

  const { data: brandingData } = useQuery({
    queryKey: ['public-branding'],
    queryFn: () => api.get('/branding/public').then((res) => res.data?.data),
    staleTime: 5 * 60 * 1000,
  })

  const devices = Array.isArray(deviceData) ? deviceData : []
  const deviceCount = devices.length
  const recentDevices = [...devices]
    .sort(
      (a: any, b: any) =>
        new Date(b.lastPingAt || b.lastActive || 0).getTime() -
        new Date(a.lastPingAt || a.lastActive || 0).getTime()
    )
    .slice(0, 3)

  const getFullImageUrl = (logoUrl: string) => {
    if (!logoUrl) return ''
    if (logoUrl.startsWith('http')) return logoUrl
    const baseUrl = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8011'
    return `${baseUrl.replace('/api', '')}${logoUrl}`
  }

  const applicationName = brandingData?.applicationName || 'redgeGate'
  const tagline = brandingData?.tagline || 'SMS & WhatsApp Gateway'
  const logoUrl = brandingData?.logoUrl ? getFullImageUrl(brandingData.logoUrl) : null

  const navigation: NavigationItem[] = [
    { name: 'Overview', href: '/dashboard', icon: Home },
    { name: 'Messages', href: '/dashboard/messages', icon: MessageSquare },
    { name: 'Senders', href: '/dashboard/devices', icon: Smartphone, badge: deviceCount > 0 ? deviceCount : undefined },
    { name: 'API keys', href: '/dashboard/api', icon: Key },
    { name: 'Webhooks', href: '/dashboard/webhooks', icon: Webhook },
    { name: 'Settings', href: '/dashboard/settings', icon: Settings },
  ]

  const isCurrentPath = (href: string) => {
    if (href === '/dashboard') {
      return location.pathname === '/dashboard' || location.pathname === '/dashboard/'
    }
    return location.pathname.startsWith(href)
  }

  return (
    <div className="flex h-full flex-col bg-white border-r border-gray-200 w-72">
      {/* Branding Header */}
      <div className="flex items-center justify-between p-5 border-b border-gray-100 flex-shrink-0">
        <div className="flex items-center space-x-3">
          {logoUrl ? (
            <img src={logoUrl} alt="Logo" className="h-9 w-9 rounded-lg object-contain" />
          ) : (
            <div className="h-9 w-9 bg-ink rounded-lg flex items-center justify-center flex-shrink-0">
              <MessageSquare className="h-5 w-5 text-white" />
            </div>
          )}
          <div className="min-w-0">
            <h1 className="text-base font-bold text-gray-900 truncate">{applicationName}</h1>
            <p className="text-xs text-gray-500 truncate">{tagline}</p>
          </div>
        </div>
        {/* Close button — only shown when rendered as mobile overlay */}
        {onClose && (
          <button
            onClick={onClose}
            className="p-1.5 rounded-md text-gray-500 hover:bg-secondary hover:text-ink flex-shrink-0"
            aria-label="Close menu"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      {/* Navigation */}
      <div className="flex-1 overflow-y-auto px-4 py-5">
        <div className="space-y-7">
          {/* Main Navigation */}
          <div>
            <nav className="space-y-0.5" aria-label="Main">
              {navigation.map((item) => {
                const current = isCurrentPath(item.href)
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    onClick={onClose}
                    className={cn(
                      'group flex items-center px-3 py-2.5 text-sm rounded-lg border-l-[3px] transition-colors',
                      current
                        ? 'bg-secondary text-ink font-semibold border-ink'
                        : 'text-gray-700 font-medium border-transparent hover:bg-secondary hover:text-ink'
                    )}
                  >
                    <item.icon
                      className={cn(
                        'mr-3 h-5 w-5 flex-shrink-0 transition-colors',
                        current ? 'text-ink' : 'text-gray-400 group-hover:text-gray-600'
                      )}
                      aria-hidden="true"
                    />
                    <span className="flex-1">{item.name}</span>
                    {item.badge && (
                      <span className={cn(
                        'ml-auto inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                        'bg-secondary text-ink'
                      )}>
                        {item.badge}
                      </span>
                    )}
                  </Link>
                )
              })}
            </nav>
          </div>

          {/* Recent Devices */}
          <div>
            <button
              onClick={() => setShowRecentDevices(!showRecentDevices)}
              className="flex items-center justify-between w-full text-sm font-semibold text-ink mb-2 px-1"
              aria-expanded={showRecentDevices}
            >
              <span>Senders</span>
              <ChevronDown className={cn('h-4 w-4 transition-transform duration-200', !showRecentDevices && '-rotate-90')} />
            </button>

            {showRecentDevices && (
              <div className="space-y-0.5">
                {recentDevices.length > 0 ? (
                  recentDevices.map((device: any) => {
                    const { state, detail } = getDeviceState(device)
                    return (
                      <Link
                        key={device.id}
                        to={`/dashboard/devices/${device.id}`}
                        onClick={onClose}
                        className="group flex items-center px-3 py-2 text-sm text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                      >
                        <span className={cn('mr-3 h-8 w-[3px] rounded-full flex-shrink-0', channelStripeClass(device))} aria-hidden="true" />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-ink truncate">{getDeviceLabel(device)}</p>
                          <p className="text-xs text-gray-500 flex items-center gap-1.5">
                            <span className={cn('h-2 w-2 rounded-full flex-shrink-0', stateDotClass[state])} aria-hidden="true" />
                            {detail}
                          </p>
                        </div>
                      </Link>
                    )
                  })
                ) : (
                  <p className="px-3 py-2 text-sm text-gray-400">No senders yet</p>
                )}

                <Link
                  to="/dashboard/devices"
                  onClick={onClose}
                  className="flex items-center px-3 py-2 text-sm text-gray-500 rounded-lg hover:bg-gray-50 hover:text-gray-700 border-2 border-dashed border-gray-200 hover:border-gray-300 mt-1 transition-colors"
                >
                  <Plus className="mr-3 h-4 w-4 flex-shrink-0" />
                  <span>Add a sender</span>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* User Profile */}
      <div className="flex-shrink-0 p-4 border-t border-gray-100">
        <div className="flex items-center gap-3">
          <div className="h-8 w-8 rounded-full bg-ink flex items-center justify-center flex-shrink-0">
            <User className="h-4 w-4 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-gray-900 truncate">{user?.name || 'User'}</p>
            <p className="text-xs text-gray-500 truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => { logout(); onClose?.() }}
            className="p-1.5 text-gray-400 hover:text-gray-600 flex-shrink-0"
            aria-label="Sign out"
          >
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  )
}
